import React, { useState, useEffect, useRef } from 'react';

import {
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';

import LocationOnIcon from '@mui/icons-material/LocationOn';
import MyLocationIcon from '@mui/icons-material/MyLocation';

import SearchIcon from '@mui/icons-material/Search';

import Autocomplete from '@mui/material/Autocomplete';

import parse from 'autosuggest-highlight/parse';
import throttle from 'lodash/throttle';

const autocompleteService = { current: null };
const geocoder = { current: null };


export default function LocationSearch({
  initialAddress,
  setWhere,
  handleNextStep,
  isLight,
  activeStep,
}) {
  
  const [inputValue, setInputValue] = useState(initialAddress);
  const [value, setValue] = useState(initialAddress);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingGPS, setLoadingGPS] = useState(false);

  const inputRef = useRef();

  const handleChange = event => {
    setInputValue(event.target.value);
  };

  const handlePlaceChange = place => {
    if (place) {
      setValue(place.structured_formatting.main_text);
      getCoords(place);
    } else {
      setValue('');
    }
    // props.onInputChange(mainText);
  };

  const getCoords = place => {
    geocoder.current.geocode({ placeId: place.place_id }, function (results, status) {
      if (status !== 'OK') {
        window.alert('Geocoder failed due to: ' + status);
        return;
      }

      setWhere(prevState => ({
        ...prevState,
        address: place.structured_formatting.main_text,
        fullAddress: place.description,
        center: {
          lat: results[0].geometry.location.lat(),
          lng: results[0].geometry.location.lng(),
        },
      }));
      if (handleNextStep) handleNextStep(3);
    });
  };

  const getAddress = ({ latitude, longitude }) => {
    const latLng = {
      lat: latitude,
      lng: longitude,
    };
    geocoder.current.geocode({ location: latLng }, function (results, status) {
      if (status === 'OK') {
        if (results[0]) {
          const shortAddress = `${results[0].address_components[0].long_name} ${results[0].address_components[1].long_name}`;
          setValue(shortAddress);
          setWhere(prevState => ({
            ...prevState,
            address: shortAddress,
            fullAddress: results[0].formatted_address,
            center: latLng,
          }));
          if (handleNextStep) handleNextStep(3);
        } else {
          setLoading(false);
          setLoadingGPS(false);
          window.alert('No results found');
        }
      } else {
        setLoading(false);
        setLoadingGPS(false);
        window.alert('Geocoder failed due to: ' + status);
      }
      if (activeStep !== 2) {
        setLoading(false);
        setLoadingGPS(false);
      }
      inputRef?.current?.blur();
    });
  };

  const geolocate = () => {
    inputRef.current.blur();
    setValue('');
    setLoading(true);
    setLoadingGPS(true);
    navigator.geolocation.getCurrentPosition(position => {
      getAddress(position.coords);
    });
  };

  useEffect(() => {
    autocompleteService.current = new window.google.maps.places.AutocompleteService();
    geocoder.current = new google.maps.Geocoder();
    if (!inputValue || inputValue != initialAddress) {
      inputRef.current.focus();
    }
  }, [initialAddress, inputValue]);

  const fetch = React.useMemo(
    () =>
      throttle((request, callback) => {
        setLoading(true);
        autocompleteService.current.getPlacePredictions(
          {
            ...request,
            componentRestrictions: { country: 'za' },
            // types: [''],
          },
          callback
        );
      }, 200),
    []
  );

  useEffect(() => {
    // Add basic option of use current location
    let active = true;

    if (!inputValue || inputValue === initialAddress) {
      setOptions([]);
      setLoading(false);
      return undefined;
    }

    fetch({ input: inputValue }, results => {
      if (active) {
        setLoading(false);
        setOptions(results || []);
      }
    });

    return () => {
      active = false;
    };
  }, [inputValue, fetch, initialAddress]);

  return (
    <Autocomplete
      value={value}
      getOptionLabel={option => (typeof option === 'string' ? option : option.description)}
      isOptionEqualToValue={(option, value) => option.structured_formatting.main_text === value}
      filterOptions={x => x}
      options={options}
      loading={loading}
      disabled={loadingGPS}
      loadingText={'Loading...'}
      noOptionsText={'Type to search'}
      autoComplete
      includeInputInList
      style={{ backgroundColor: isLight ? '#fff' : 'unset' }}
      openOnFocus={true}
      popupIcon={!loadingGPS ? <SearchIcon /> : <CircularProgress size={24} />}
      forcePopupIcon
      renderInput={params => (
        <TextField
          {...params}
          placeholder={loadingGPS ? 'Finding your location' : 'Enter your service location'}
          variant="outlined"
          fullWidth
          onChange={handleChange}
          inputRef={inputRef}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <InputAdornment position="start">
                  <IconButton
                    edge="start"
                    onClick={event => {
                      event.stopPropagation();
                      geolocate();
                    }}
                    disabled={loadingGPS}
                    size="large">
                    <MyLocationIcon />
                  </IconButton>
                </InputAdornment>
                {params.InputProps.startAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => {
        const matches = option.structured_formatting.main_text_matched_substrings;
        const parts = parse(
          option.structured_formatting.main_text,
          matches.map(match => [match.offset, match.offset + match.length])
        );

        return (
          <Grid container alignItems="center" {...props}>
            <Grid item>
              <LocationOnIcon
                sx={{
                  color: 'text.secondary',
                  mr: 2,
                }}
              />
            </Grid>
            <Grid item xs>
              {parts.map((part, index) => (
                <span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
                  {part.text}
                </span>
              ))}

              <Typography variant="body2" color="textSecondary">
                {option.structured_formatting.secondary_text}
              </Typography>
            </Grid>
          </Grid>
        );
      }}
      onChange={(event, value) => {
        handlePlaceChange(value);
      }}
      sx={{
        borderRadius: 2,
        '& .MuiAutocomplete-popupIndicatorOpen': {
          transform: 'none',
        },
        // '&.MuiAutocomplete-listbox': {
        //   backgroundImage: "url('/powered_by_google_on_white.png')",
        //   backgroundRepeat: 'no-repeat',
        //   backgroundPosition: 'right bottom',
        // },
        '& .MuiAutocomplete-popper': {
          backgroundImage: "url('/powered_by_google_on_white.png')",
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right bottom',
        }
      }}
    />
  );
}
